import { useEffect } from "react";

const STORAGE_KEY = "fc_utm";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
type UtmKey = typeof UTM_KEYS[number];

export interface UtmPayload {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  landing_page?: string;
  captured_at?: string;
}

/**
 * Captures UTM params from the URL on app mount and stores them in localStorage.
 * Only overwrites if a NEW utm_source/utm_campaign is present — so a direct
 * visit after a paid click doesn't wipe the attribution.
 *
 * Call once near the root of the app (e.g. in AppRoutes).
 */
export function useUtmTracking() {
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const incoming: UtmPayload = {};
      let hasAny = false;
      UTM_KEYS.forEach((key) => {
        const v = params.get(key);
        if (v) {
          (incoming as any)[key] = v;
          hasAny = true;
        }
      });
      if (!hasAny) return;

      incoming.landing_page = window.location.pathname;
      incoming.captured_at = new Date().toISOString();

      const existingRaw = localStorage.getItem(STORAGE_KEY);
      const existing = existingRaw ? (JSON.parse(existingRaw) as UtmPayload) : null;

      // Only overwrite if the new click has a new source/campaign —
      // otherwise keep the original first-touch attribution.
      if (!existing || incoming.utm_source !== existing.utm_source || incoming.utm_campaign !== existing.utm_campaign) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(incoming));
      }
    } catch (err) {
      console.warn("[utm] capture failed:", err);
    }
  }, []);
}

export function getUtmPayload(): UtmPayload | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as UtmPayload) : null;
  } catch {
    return null;
  }
}

export function clearUtmPayload() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {
    // localStorage can throw in private mode — nothing to clear in that case.
  }
}
